/**
 * Tailwind CSS class name utility
 * 
 * Combines clsx and tailwind-merge to intelligently merge Tailwind CSS classes.
 * Handles conditional classes and resolves conflicts (e.g., 'p-4' and 'p-2' → 'p-2').
 * 
 * @example
 * ```tsx
 * import { cn } from '@/utils/twMerge'
 * 
 * <div className={cn(
 *   'base-class',
 *   isActive && 'active-class',
 *   variant === 'primary' && 'bg-blue-500',
 *   className // Allow override
 * )} />
 * 
 * // Conditional classes
 * <button className={cn('btn', error && 'btn-error', disabled && 'opacity-50')} />
 * 
 * // Class conflicts resolved
 * <div className={cn('p-4', 'p-2')} /> // Results in 'p-2' (last one wins)
 * ```
 */
import { twMerge } from 'tailwind-merge'
import { clsx, type ClassValue } from 'clsx'

/**
 * Intelligently merges Tailwind CSS class names
 * 
 * Combines clsx for conditional classes and tailwind-merge for conflict resolution.
 * This ensures that conflicting Tailwind classes are properly resolved (e.g., 'p-4' and 'p-2').
 * 
 * @param classes - Variable number of class values (strings, objects, arrays, etc.)
 * @returns Merged class string with conflicts resolved
 * 
 * @example
 * ```ts
 * cn('base', isActive && 'active', 'p-4', 'p-2') // Returns: 'base active p-2'
 * cn({ 'text-red': hasError }, 'text-blue') // Returns: 'text-blue' (conflict resolved)
 * ```
 */
export const cn = (...classes: ClassValue[]) => twMerge(clsx(...classes))
